import { Moment } from "moment";
import { Observable, throwError } from "rxjs";
import { switchMap } from "rxjs/operators";
import { httpClient, _unwrapAxiosResponse } from "./http";
import { Studio } from "./Studio";
import { Dame, Termin, TermineService } from "./Termine";

export interface MonatsabschlussZeile {
  monat_studio?: string;
  dame?: Dame;
  anzahlDienste: number;
  anzahlDiensteZusammengelegt: number;
  anzahlDiensteSteuerpflichtig: number;
  miete: number;
  steuer: number;
  tage?: AbrechnungTag[];
  datum?: string;
  buchhalter?: string;
  rechnungsnummer?: string;
  rechnungsdatum?: string;
}

export interface AbrechnungTermin {
  /** ID-Nummer des Termins */
  termin: number;
  /** Datum, wann der Termin in der Buchhaltung aufschlägt (um ggfs. die Tagessteuer zu sparen); Format YYYY-MM-DD */
  datum: string;
  /** Dauer in Stunden (ggfs. mit Nachkommastellen) */
  dauer: number;
  /** Berechnete Miete für den Termin in € nach vereinbartem Gebührenplan */
  miete: number;
}
export interface AbrechnungTag {
  /** Format: DD */
  tag: string;
  /** Format: DD */
  buchungstag: string;
  /** In den Tag einfließende Termin-Abrechnungen */
  anzahlTermine: number;
  /** Berechnete Miete für den gesamten Tag lt. Gebührenplan */
  miete: number;
  /** Zu erhebende Steuer nach düsseldorfer Verfahren für den Arbeitstag (0€ oder 25€) */
  steuer: number;
  /** Manuelle Anpassung resultierend aus prozentualem Monatseinnahmen-Verschiebe-Betrag von einem Studio hin zu einem anderen */
  manuelleAnpassung?: number;
  /** Base64-codierte Bild-URL der Unterschrift der Dame */
  unterschriftDame?: string;
  /** Base64-codierte Bild-URL der Unterschrift des Studios, bzw. Betreibers */
  unterschriftStudio?: string;
  /** Besonderer Hinweis, z.B. aus manueller Bearbeitung */
  hinweis?: string;
  /** Besonderer Status, z.B. "zu prüfen" */
  status?: TerminStatus;
}

export type TerminStatus = "zu prüfen";

export interface CalendarDay extends AbrechnungTag {
  renderKey?: string;
}
export interface Summe {
  summe: number;
  summeDdSteuer: number;
  anzahlTage: number;
}
export interface CalendarDayGroup extends Summe {
  wochenIndex: number;
  days: Array<CalendarDay>;
}
export type CalendarDayGroups = Array<CalendarDayGroup>;

/* (Temporärer) Faktor für Berechnung von Preis, pauschaler und vereinamter Umsatzsteuer */
const PREISFAKTOR = 0.5;

export interface Kassenbuch {
  monat: string;
  blatt: number;
  ausgaben: Array<Ausgabe>;
  kassenbuch: Kassenbuch;
  saldo: number;
}

export type TransaktionArt = 'einnahme' | 'ausgabe';

export interface Einnahme {
  tag: number;
  studio: string;
  bezeichnung: number;
  einnahme: number;
  steuer: number;
}

export interface Ausgabe {
  tag: number;
  bezeichnung: string;
  beleg?: number;
  betrag: number;
}

export type Transaktion = (Einnahme | Ausgabe) & {
  typ: TransaktionArt;
};

export function vollerPreisDesTermins(termin: Termin) {
  let preis = 0;
  let restDauer = termin.dauer;
  while (restDauer >= 1) {
    if (restDauer == 1.25 && termin.massageart?.preis45) { // Spezialfall, weil wir keine Preise für 1.25 Stunden haben
      preis += termin.massageart?.preis45;
      restDauer -= 0.75;
    } else {
      preis += termin.massageart?.preis60 || 0;
      restDauer -= 1;
    }
  }
  while (restDauer >= 0.75) {
    preis += termin.massageart?.preis45 || termin.massageart?.preis30 || termin.massageart?.preis60 || 0;
    restDauer -= 0.75;
  }
  while (restDauer >= 0.5) {
    preis += termin.massageart?.preis30 || termin.massageart?.preis60 || 0;
    restDauer -= 0.5;
  }
  if (termin.massageart?.zweiteDame) {
    preis /= 2;
  }

  return preis;
}

export function berechneMiete(termin: Termin, manuellePreiseIgnorieren = false): number {
    function ohneNachkommastellen(miete: number): number {
      // Gemäß Stephans Wunsch vom 05.09.2021 sollen durch Anwendung des Faktors (z.B. Halbierung)
      // keine krummen Preise entstehen. In diesem Fall soll nicht nur der Nachkommateil wegfallen,
      // sondern auch die Ziffer davor (die Einerstelle) genullt werden
      if (miete - Math.floor(miete) > 0) {
        miete = Math.floor(miete / 10) * 10;
      }
      return miete;
    }
    if (!manuellePreiseIgnorieren && termin.preisManuell) {
      return ohneNachkommastellen(termin.preisManuell * PREISFAKTOR);
    }

    const miete = vollerPreisDesTermins(termin);
    return ohneNachkommastellen(miete * PREISFAKTOR);
  }

export function berechneSteuer(dame: Dame, tagesMiete: number): number {
  return dame.steuernummer ? 0 : tagesMiete < 100 ? 15 : 25;
}

export class AbrechnungService {
  constructor(private termineService: TermineService) {}

  public monatsabschlussNeuBerechnen(studio: Studio, monat: Moment, alleDamenZurueckgeben: boolean, dame?: Dame): Observable<MonatsabschlussZeile[]> {
    if (!studio || !studio.bezeichner) {
      return throwError("Ungültiges Studio angegeben");
    }
    const datumString = monat.format().substring(0, 7);
    let url = `/studio/${studio.bezeichner}/monat?monat=${datumString}`;
    if (dame && dame.id) {
      url += `&dame=${dame.id}`;
    }
    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: ["Buchhaltung/Loeschen"],
    } as any;

    return _unwrapAxiosResponse<void>(httpClient.delete(url, scopeConfig))
      .pipe(
        switchMap(signal => this.termineService.getTermineMonat(studio, monat, alleDamenZurueckgeben, dame))
      );
  }
  public updateMonatsabschlussDame(studio: Studio, tag: Moment, dame: number, miete: number): Observable<string[]> {
    if (!studio || !studio.bezeichner) {
      return throwError("Ungültiges Studio angegeben");
    }
    const url = `/studio/${studio.bezeichner}/monat`;
    const datumString = tag.format().substring(0, 10);
    const data = {
      tag: datumString,
      dame,
      miete
    };
    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: ["Buchhaltung"],
    } as any;

    return _unwrapAxiosResponse<string[]>(httpClient.post(url, data, scopeConfig));
  }
  public monatseinnahmenVerschieben(monat: Moment, sourceStudio: Studio, targetStudio: Studio, betrag: number): Observable<string[]> {
    if (!sourceStudio || !sourceStudio.bezeichner) {
      return throwError("Ungültiges Studio angegeben");
    }
    if (!targetStudio || !targetStudio.bezeichner) {
      return throwError("Ungültiges Studio angegeben");
    }
    const url = `/studio/${sourceStudio.bezeichner}/einnahmen-verschieben`;
    const data = {
      monat: monat.format().substring(0, 7),
      targetStudio: targetStudio.bezeichner,
      betrag
    };
    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: ["Buchhaltung"],
    } as any;

    return _unwrapAxiosResponse<string[]>(httpClient.post(url, data, scopeConfig));
  }
}

export class KassenbuchService {
  public getKassenbuch(monat: Moment) {
    const url = `/kassenbuch/${monat.format('YYYY-MM')}`;
    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: ["Buchhaltung"],
    } as any;
    return _unwrapAxiosResponse<Kassenbuch>(httpClient.get(url, scopeConfig));
  }
  public getKassenbuchEinnahmen(monat: Moment) {
    const url = `/kassenbuch/${monat.format('YYYY-MM')}/einnahmen`;
    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: ["Buchhaltung"],
    } as any;
    return _unwrapAxiosResponse<MonatsabschlussZeile[]>(httpClient.get(url, scopeConfig));
  }
  public getKassenbuchAusgaben(monat: Moment) {
    const url = `/kassenbuch/${monat.format('YYYY-MM')}/ausgaben`;
    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: ["Buchhaltung"],
    } as any;
    return _unwrapAxiosResponse<Ausgabe[]>(httpClient.get(url, scopeConfig));
  }
  public updateKassenbuch(monat: Moment, kassenbuch: Kassenbuch) {
    kassenbuch.monat = monat.format('YYYY-MM');
    const url = `/kassenbuch/${kassenbuch.monat}`;
    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: ["Buchhaltung"],
    } as any;
    return _unwrapAxiosResponse<string[]>(httpClient.post(url, kassenbuch, scopeConfig));
  }
}
